import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Alert } from '../components/atoms/Alert/Alert';
import { Badge } from '../components/atoms/Badge/Badge';
import { Button } from '../components/atoms/Button/Button';
import { Card } from '../components/atoms/Card/Card';
import { EmptyState } from '../components/atoms/EmptyState';
import { Inline } from '../components/atoms/Inline/Inline';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { Select } from '../components/atoms/Select/Select';
import { Stack } from '../components/atoms/Stack/Stack';
import { Heading } from '../components/atoms/Typography/Heading';
import { Text } from '../components/atoms/Typography/Text';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { Dialog } from '../components/ui/Dialog/Dialog';
import { StartQuarantineForm, type QuarantineAnimal } from '../components/quarantine/StartQuarantineForm';
import { TreatmentSensitivityNotice } from '../components/quarantine/TreatmentSensitivityNotice';
import { taskTitles } from '../components/quarantine/quarantineTitles';
import { listLivestock } from '../services/livestockService';
import {
  completeQuarantineStep,
  endQuarantine,
  listQuarantine,
  quarantineKey,
  startQuarantine,
  type QuarantineProtocol,
} from '../services/quarantineService';
import '../components/quarantine/Quarantine.css';

function formatDate(day: string, language: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1));
}

/** One running or finished protocol: steps in order (tick when due), finish / abort. */
function ProtocolCard({ protocol, aquariumId, onChanged }: { protocol: QuarantineProtocol; aquariumId: string; onChanged: (msg: string) => void }) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const queryClient = useQueryClient();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: quarantineKey(aquariumId) });
  const step = useMutation({
    mutationFn: (stepId: string) => completeQuarantineStep(aquariumId, protocol.id, stepId),
    onSuccess: () => {
      refresh();
      onChanged(String(t('quarantine.card.stepDone')));
    },
  });
  const end = useMutation({
    mutationFn: (outcome: 'finish' | 'abort') => endQuarantine(aquariumId, protocol.id, outcome),
    onSuccess: (_p, outcome) => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['livestock'] });
      onChanged(String(t(outcome === 'finish' ? 'quarantine.card.finished' : 'quarantine.card.aborted')));
    },
  });
  const active = protocol.status === 'active';
  const open = protocol.steps.filter((s) => !s.completed).length;
  const title = protocol.livestockName
    ? t('quarantine.card.title', { protocol: t(`quarantine.protocols.${protocol.protocolType}.name`), name: protocol.livestockName })
    : t(`quarantine.protocols.${protocol.protocolType}.name`);
  return (
    <li data-testid={`quarantine-${protocol.id}`}>
      <Card>
        <Stack gap="3">
          <Inline gap="2" align="center" wrap>
            <Heading level={3}>{title}</Heading>
            <Badge tone={active ? 'warning' : protocol.status === 'completed' ? 'success' : 'neutral'}>{t(`quarantine.status.${protocol.status}`)}</Badge>
          </Inline>
          <Text color="secondary">
            {t('quarantine.card.started', { date: formatDate(protocol.startedAt.slice(0, 10), language) })}
            {protocol.confidence !== 'none' && ` · ${String(t('quarantine.card.confidence', { confidence: t(`reefGuard.confidence.${protocol.confidence === 'high' ? 'high' : 'medium'}`) }))}`}
          </Text>
          {protocol.protocolType === 'ttm' && active && <Alert tone="warning" live="off" compact>{t('quarantine.protocols.ttm.caveat')}</Alert>}
          {active && (
            <TreatmentSensitivityNotice sensitivities={protocol.sensitivities ?? []} copperProtocol={protocol.protocolType === 'copper'} compact />
          )}
          {protocol.protocolType === 'fallow' && active && <Text>{t('quarantine.protocols.fallow.hedge')}</Text>}
          {(step.isError || end.isError) && <Alert compact>{t('quarantine.card.error')}</Alert>}
          <ol className="quarantine__steps">
            {protocol.steps.map((s) => (
              <li key={s.id} className="quarantine__step">
                <Stack gap="1" className="quarantine__step-text">
                  <Text weight="semibold">{t(`quarantine.steps.${s.key}.title`)}</Text>
                  <Text color="secondary" size="sm">
                    {t(`quarantine.steps.${s.key}.what`, { days: protocol.config.dipIntervalDays ?? 14 })} · {t('quarantine.card.due', { date: formatDate(s.dueDate, language) })}
                  </Text>
                </Stack>
                {s.completed ? (
                  <Badge tone="success">{t('quarantine.card.done')}</Badge>
                ) : active && s.available ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => step.mutate(s.id)}
                    disabled={step.isPending}
                    aria-label={t('quarantine.card.completeLabel', { step: t(`quarantine.steps.${s.key}.title`) })}
                  >
                    {t('quarantine.card.complete')}
                  </Button>
                ) : (
                  active && <Badge tone="neutral">{t('quarantine.card.later')}</Badge>
                )}
              </li>
            ))}
          </ol>
          {active && (
            <Inline gap="2" wrap>
              <Button variant="secondary" onClick={() => end.mutate('finish')} disabled={end.isPending}>
                {t(protocol.livestockId ? 'quarantine.card.finish' : 'quarantine.card.finishFallow')}
              </Button>
              <Button variant="ghost" onClick={() => end.mutate('abort')} disabled={end.isPending}>
                {t('quarantine.card.abort')}
              </Button>
              {open > 0 && <Text color="muted" variant="caption">{t('quarantine.card.openSteps', { count: open, steps: open })}</Text>}
            </Inline>
          )}
        </Stack>
      </Card>
    </li>
  );
}

/**
 * "Quarantäne" (Reef Guard #1826): running and finished protocols with their
 * steps, starting a protocol for an animal, a fallow period for the display
 * tank, and a guide for quarantine without a separate tank. No doses —
 * medication is dosed from the product label.
 */
export function QuarantinePage() {
  const { t } = useTranslation();
  const { aquariumId = '' } = useParams<{ aquariumId: string }>();
  const queryClient = useQueryClient();
  const [announcement, setAnnouncement] = useState('');
  const [startOpen, setStartOpen] = useState(false);
  const [animalId, setAnimalId] = useState('');
  const query = useQuery({ queryKey: quarantineKey(aquariumId), queryFn: () => listQuarantine(aquariumId), enabled: Boolean(aquariumId) });
  const livestock = useQuery({ queryKey: ['livestock', aquariumId, 'quarantine-candidates'], queryFn: () => listLivestock(aquariumId, { status: 'active' }), enabled: startOpen });
  const fallow = useMutation({
    mutationFn: () => startQuarantine(aquariumId, { protocol_type: 'fallow', task_titles: taskTitles(t, 'fallow', null) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: quarantineKey(aquariumId) });
      setAnnouncement(String(t('quarantine.fallow.started')));
    },
  });

  const protocols = query.data ?? [];
  const running = protocols.filter((p) => p.status === 'active');
  const finished = protocols.filter((p) => p.status !== 'active');
  const fallowRunning = running.some((p) => p.protocolType === 'fallow');
  const candidates = (livestock.data ?? []).filter((a) => a.category !== ('plant' as string));
  const chosen = candidates.find((a) => a.id === animalId);
  const animal: QuarantineAnimal | null = chosen
    ? { id: chosen.id, name: chosen.commonName, category: chosen.category as QuarantineAnimal['category'] }
    : null;

  let body;
  if (query.isPending) {
    body = <LiveRegion busy><Text color="muted">{t('quarantine.loading')}</Text></LiveRegion>;
  } else if (query.isError) {
    body = <LoadErrorState title={t('quarantine.loadError')} onRetry={() => void query.refetch()} retryLabel={t('quarantine.retry')} titleLevel={3} />;
  } else if (running.length === 0) {
    body = (
      <EmptyState
        icon={<ShieldCheck aria-hidden="true" />}
        headline={t('quarantine.empty.title')}
        description={t('quarantine.empty.description')}
        actionLabel={t('quarantine.start.open')}
        onAction={() => setStartOpen(true)}
        headingLevel={3}
      />
    );
  } else {
    body = (
      <ul className="quarantine__list" data-testid="quarantine-running">
        {running.map((p) => <ProtocolCard key={p.id} protocol={p} aquariumId={aquariumId} onChanged={setAnnouncement} />)}
      </ul>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default">
          <PageHeader
            title={t('quarantine.title')}
            description={t('quarantine.subtitle')}
            actions={<Button onClick={() => setStartOpen(true)}>{t('quarantine.start.open')}</Button>}
          />
          <LiveRegion visuallyHidden>{announcement}</LiveRegion>
          <Section title={t('quarantine.section.running')}>{body}</Section>
          <Section title={t('quarantine.fallow.title')} description={t('quarantine.fallow.description')}>
            <Card>
              <Stack gap="3">
                <Text>{t('quarantine.protocols.fallow.summary')}</Text>
                {fallow.isError && <Alert compact>{t('quarantine.start.error')}</Alert>}
                <Inline gap="2">
                  <Button variant="secondary" onClick={() => fallow.mutate()} disabled={fallow.isPending || fallowRunning}>
                    {t(fallowRunning ? 'quarantine.fallow.running' : 'quarantine.fallow.start')}
                  </Button>
                </Inline>
              </Stack>
            </Card>
          </Section>
          <Section title={t('quarantine.minimal.title')}>
            <Card>
              <Stack gap="2">
                <Text>{t('quarantine.minimal.intro')}</Text>
                <ul className="quarantine__tips">
                  {(['container', 'observe', 'dip', 'separate', 'no_meds'] as const).map((k) => (
                    <li key={k}><Text>{t(`quarantine.minimal.tips.${k}`)}</Text></li>
                  ))}
                </ul>
                <Text color="muted" variant="caption">{t('quarantine.noDoses')}</Text>
              </Stack>
            </Card>
          </Section>
          {finished.length > 0 && (
            <Section title={t('quarantine.section.finished')}>
              <ul className="quarantine__list" data-testid="quarantine-finished">
                {finished.map((p) => <ProtocolCard key={p.id} protocol={p} aquariumId={aquariumId} onChanged={setAnnouncement} />)}
              </ul>
            </Section>
          )}
          <Dialog
            isOpen={startOpen}
            onClose={() => {
              setStartOpen(false);
              setAnimalId('');
            }}
            title={t('quarantine.start.title')}
            size="sm"
            data-testid="start-quarantine-dialog"
          >
            <Stack gap="4">
              {livestock.isError && <Alert>{t('quarantine.start.loadError')}</Alert>}
              <Select
                id="quarantine-animal"
                label={t('quarantine.start.animal')}
                value={animalId}
                options={[
                  { value: '', label: String(t('quarantine.start.animalPlaceholder')) },
                  ...candidates.map((a) => ({ value: a.id, label: a.commonName })),
                ]}
                onChange={(e) => setAnimalId(e.target.value)}
              />
              {livestock.isSuccess && candidates.length === 0 && <Text color="muted">{t('quarantine.start.noAnimals')}</Text>}
              {animal && (
                <StartQuarantineForm
                  key={animal.id}
                  aquariumId={aquariumId}
                  animal={animal}
                  onStarted={() => {
                    setStartOpen(false);
                    setAnimalId('');
                    setAnnouncement(String(t('quarantine.start.started')));
                    void queryClient.invalidateQueries({ queryKey: quarantineKey(aquariumId) });
                  }}
                  onCancel={() => {
                    setStartOpen(false);
                    setAnimalId('');
                  }}
                />
              )}
            </Stack>
          </Dialog>
        </PageContent>
      }
    />
  );
}

export default QuarantinePage;
